import React from "react";
import { cn } from "../../lib/utils";
import { WorkspaceHeader } from "../shell/WorkspaceHeader";
import { ContentFrame } from "../shell/ContentFrame";
import type { 
  ApiGatewayManagementWorkspaceProps, 
  ApiGatewayKeyRecord,
  ApiGatewayManagementNotice,
  ApiGatewayManagementStat,
  ApiGatewayTenantPickerProps,
  ApiGatewayManagementTone,
} from "../../contracts/api-gateway-management";
import { Input } from "../../internal/primitives/input";

/**
 * ApiGatewayManagementWorkspace is the primary shared workspace for managing API keys.
 * It supports both self-service (portal) and administrative (admin) modes.
 */
export function ApiGatewayManagementWorkspace({
  mode,
  title,
  description,
  eyebrow,
  contextSummary,
  tenantPicker,
  notices = [],
  stats = [],
  actions = [],
  docsActions = [],
  keys = [],
  loading,
  errorTitle,
  errorDescription,
  emptyTitle,
  emptyDescription,
  selectedKeyId,
  createActionLabel = "Create new key",
  revokeActionLabel = "Revoke key",
  revokingKeyId,
  onAction,
  onDocsAction,
  onSelectKey,
  onCreateKey,
  onRequestRevoke,
  className,
}: ApiGatewayManagementWorkspaceProps) {
  const workspaceActions = createActionLabel && onCreateKey
    ? [
        {
          id: "create",
          label: createActionLabel,
          tone: "primary" as const,
        },
      ]
    : [];

  return (
    <div className={cn("flex flex-col h-full bg-background", className)}>
      <WorkspaceHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        metaItems={contextSummary.items?.map(item => ({
          id: item.id,
          label: item.label,
          value: item.value
        }))}
        actions={workspaceActions}
        onAction={(id) => {
          if (id === "create") onCreateKey?.();
        }}
      />

      <ContentFrame
        padded
        className="min-h-0 flex-1"
        bodyClassName="overflow-y-auto"
        asideClassName="overflow-y-auto"
        aside={
          <div className="flex flex-col gap-8 h-full">
            {/* Context Summary */}
            <div className="space-y-4">
              <div className="space-y-1">
                {contextSummary.eyebrow && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {contextSummary.eyebrow}
                  </span>
                )}
                <h3 className="text-sm font-bold text-foreground">
                  {contextSummary.title}
                </h3>
                {contextSummary.description && (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {contextSummary.description}
                  </p>
                )}
              </div>

              {contextSummary.badges && contextSummary.badges.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {contextSummary.badges.map(badge => (
                    <Badge key={badge.id} label={badge.label} tone={badge.tone} />
                  ))}
                </div>
              )}
            </div>

            {/* Tenant Picker (Admin Mode) */}
            {mode === "admin" && tenantPicker && (
              <div className="border-t border-border pt-8 space-y-4">
                <TenantPicker {...tenantPicker} />
              </div>
            )}

            {/* Management Actions */}
            {actions.length > 0 && (
              <div className="pt-8 border-t border-border space-y-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Management Actions
                </h4>
                <div className="space-y-1">
                  {actions.map(action => (
                    <button
                      type="button"
                      key={action.id}
                      onClick={() => onAction?.(action.id)}
                      disabled={action.disabled}
                      className={cn(
                        "w-full flex items-center gap-3 p-2 rounded-md text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50",
                        action.tone === "primary" ? "text-primary" : "text-foreground",
                        action.tone === "danger" ? "text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20" : ""
                      )}
                    >
                      {action.icon && <span className="w-3.5 h-3.5 shrink-0">{action.icon}</span>}
                      <div className="flex flex-col items-start min-w-0">
                        <span>{action.label}</span>
                        {action.description && (
                          <span className="text-[10px] text-muted-foreground font-normal truncate w-full">
                            {action.description}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Documentation Links */}
            {docsActions.length > 0 && (
              <div className="mt-auto pt-8 border-t border-border space-y-3">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Documentation & Support
                </h4>
                <div className="space-y-1">
                  {docsActions.map(action => (
                    <button
                      type="button"
                      key={action.id}
                      onClick={() => onDocsAction?.(action.id)}
                      disabled={action.disabled}
                      className="w-full flex items-center justify-between p-2 rounded-md text-xs font-medium hover:bg-muted text-foreground transition-colors disabled:opacity-50 group"
                    >
                      <span>{action.label}</span>
                      {action.icon && <span className="opacity-60 group-hover:opacity-100">{action.icon}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        }
      >
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Notices */}
          {notices.length > 0 && (
            <div className="space-y-3">
              {notices.map(notice => (
                <NoticeCard key={notice.id} {...notice} />
              ))}
            </div>
          )}

          {/* Stats */}
          {stats.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map(stat => (
                <StatCard key={stat.id} {...stat} />
              ))}
            </div>
          )}

          {/* Main Key Inventory */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold tracking-tight">API Keys</h3>
              <div className="text-xs text-muted-foreground">
                {keys.length} {keys.length === 1 ? 'key' : 'keys'} active
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 rounded-lg border border-border bg-card animate-pulse" />
                ))}
              </div>
            ) : errorTitle ? (
              <div className="p-12 text-center rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900/50">
                <h4 className="text-sm font-bold text-red-800 dark:text-red-400">{errorTitle}</h4>
                {errorDescription && <p className="mt-1 text-xs text-red-700/80 dark:text-red-400/80">{errorDescription}</p>}
              </div>
            ) : keys.length === 0 ? (
              <div className="p-12 text-center rounded-lg border border-dashed border-border bg-card/50">
                <h4 className="text-sm font-bold text-foreground">{emptyTitle || "No API keys found"}</h4>
                {emptyDescription && <p className="mt-1 text-xs text-muted-foreground">{emptyDescription}</p>}
                {createActionLabel && onCreateKey && (
                  <button 
                    type="button"
                    onClick={onCreateKey}
                    className="mt-4 px-4 py-2 text-xs font-bold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {createActionLabel}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {keys.map(key => (
                  <KeyCard 
                    key={key.id} 
                    keyRecord={key}
                    isSelected={selectedKeyId === key.id}
                    isRevoking={revokingKeyId === key.id}
                    revokeLabel={revokeActionLabel ?? "Revoke key"}
                    onSelect={() => onSelectKey?.(key.id)}
                    onRevoke={() => onRequestRevoke?.(key.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </ContentFrame>
    </div>
  );
}

// --- Sub-components ---

function Badge({ label, tone = "neutral" }: { label: string; tone?: ApiGatewayManagementTone }) {
  const toneClasses = {
    neutral: "bg-muted text-muted-foreground border-border",
    success: "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    warning: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
    danger: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
    info: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  }[tone];

  return (
    <span className={cn("px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-tight rounded-sm border", toneClasses)}>
      {label}
    </span>
  );
}

function TenantPicker({
  title = "Select Tenant",
  description,
  query,
  queryPlaceholder = "Search tenants...",
  emptyMessage = "No tenants found",
  loading,
  selectedTenantId,
  options,
  onQueryChange,
  onSelectTenant,
}: ApiGatewayTenantPickerProps) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <h4 className="text-xs font-bold text-foreground">{title}</h4>
        {description && <p className="text-[11px] text-muted-foreground leading-tight">{description}</p>}
      </div>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={queryPlaceholder ?? undefined}
          className="h-8 text-xs pr-8 bg-background"
        />
        {loading && (
          <div className="absolute right-2.5 top-2">
            <div className="w-3 h-3 rounded-full border border-border border-t-primary animate-spin" />
          </div>
        )}
      </div>
      <div className="max-h-[320px] overflow-auto rounded-md border border-border bg-background">
        {options.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground italic">
            {emptyMessage}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {options.map((option) => (
              <button
                type="button"
                key={option.id}
                onClick={() => onSelectTenant(option.id)}
                disabled={option.disabled}
                className={cn(
                  "w-full text-left p-2.5 hover:bg-muted transition-colors disabled:opacity-50",
                  selectedTenantId === option.id && "bg-accent text-accent-foreground hover:bg-accent/90"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold truncate">{option.label}</span>
                  {option.metaLabel && (
                    <span className={cn(
                      "text-[10px] font-medium opacity-60",
                      selectedTenantId === option.id ? "text-accent-foreground" : "text-muted-foreground"
                    )}>
                      {option.metaLabel}
                    </span>
                  )}
                </div>
                {option.description && (
                  <p className={cn(
                    "text-[11px] mt-0.5 line-clamp-1",
                    selectedTenantId === option.id ? "text-accent-foreground/80" : "text-muted-foreground"
                  )}>
                    {option.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoticeCard({ title, description, tone = "info" }: ApiGatewayManagementNotice) {
  const toneStyles = {
    neutral: "border-border bg-muted/50 text-muted-foreground",
    success: "border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400",
    warning: "border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400",
    danger: "border-red-200 bg-red-50 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400",
    info: "border-blue-200 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400",
  }[tone];

  return (
    <div className={cn("p-4 rounded-lg border flex flex-col gap-1", toneStyles)}>
      {title && <h5 className="text-sm font-bold leading-none">{title}</h5>}
      <p className="text-xs leading-relaxed opacity-90">{description}</p>
    </div>
  );
}

function StatCard({ label, value, description, tone = "neutral" }: ApiGatewayManagementStat) {
  const toneClasses = {
    neutral: "text-foreground",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    danger: "text-red-600 dark:text-red-400",
    info: "text-blue-600 dark:text-blue-400",
  }[tone];

  return (
    <div className="p-4 rounded-lg border border-border bg-card shadow-sm">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className={cn("text-2xl font-bold mt-1 tabular-nums", toneClasses)}>
        {value}
      </div>
      {description && (
        <p className="text-[10px] text-muted-foreground mt-1.5 line-clamp-1">
          {description}
        </p>
      )}
    </div>
  );
}

interface KeyCardProps {
  keyRecord: ApiGatewayKeyRecord;
  isSelected?: boolean;
  isRevoking?: boolean;
  revokeLabel: string;
  onSelect: () => void;
  onRevoke: () => void;
}

function KeyCard({ 
  keyRecord, 
  isSelected, 
  isRevoking, 
  revokeLabel,
  onSelect, 
  onRevoke 
}: KeyCardProps) {
  return (
    <div 
      className={cn(
        "group relative p-4 rounded-lg border transition-all cursor-pointer",
        isSelected 
          ? "border-primary bg-primary/[0.03] ring-1 ring-primary/50" 
          : "border-border bg-card hover:border-muted-foreground/30 hover:shadow-md"
      )}
      onClick={onSelect}
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        {/* Identity & Status */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-foreground truncate">
              {keyRecord.displayName}
            </h4>
            <Badge label={keyRecord.statusLabel} tone={keyRecord.statusTone} />
            {keyRecord.tags?.map(tag => (
              <Badge key={tag.id} label={tag.label} tone={tag.tone} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[11px] font-mono">
              {keyRecord.prefix}••••••••
            </code>
            {keyRecord.description && (
              <span className="text-xs text-muted-foreground truncate max-w-md">
                — {keyRecord.description}
              </span>
            )}
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase text-muted-foreground font-medium">Created</span>
            <span className="text-xs font-medium">{keyRecord.createdAtLabel}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase text-muted-foreground font-medium">Last used</span>
            <span className="text-xs font-medium">{keyRecord.lastUsedAtLabel}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase text-muted-foreground font-medium">Expires</span>
            <span className="text-xs font-medium">{keyRecord.expiresAtLabel}</span>
          </div>
        </div>

        {/* Action Reveal */}
        <div className="flex items-center gap-2 shrink-0 self-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRevoke();
            }}
            disabled={isRevoking}
            className={cn(
              "p-2 rounded-md border border-transparent text-red-600 hover:bg-red-50 hover:border-red-200 transition-all opacity-0 group-hover:opacity-100",
              isRevoking && "opacity-100 animate-pulse cursor-wait"
            )}
            title={revokeLabel}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
          </button>
        </div>
      </div>

          {/* Permissions & Secondary Details */}
      <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <span className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Permissions</span>
          <p className="text-xs text-foreground leading-tight">
            {keyRecord.permissionSummary || keyRecord.permissions.join(", ")}
          </p>
        </div>
        {keyRecord.allowedIpSummary && (
          <div className="space-y-1.5">
            <span className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Allowed IPs</span>
            <p className="text-xs text-foreground leading-tight">
              {keyRecord.allowedIpSummary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
